import { useState, useEffect } from "react";
import themeData from "../utils/theme.json";
import { environment } from "src/config/environment";

// export const pxToRem = (px: number): string => `${px / 12}rem`;

export const getAntdThemeFromCSSVariables = () => {
	const root = document.documentElement;

	return {
		colorPrimary: getComputedStyle(root).getPropertyValue("--primary-color").trim(),
		colorLink: getComputedStyle(root).getPropertyValue("--link-color").trim(),
		colorTextBase: getComputedStyle(root).getPropertyValue("--text-base-color").trim(),
		colorBgBase: getComputedStyle(root).getPropertyValue("--bg-base-color").trim(),
		// fontSize: pxToRem(parseInt(getComputedStyle(root).getPropertyValue("--font-size").trim())),
	};
};

export const useTheme = () => {
	const [theme, setTheme] = useState(getAntdThemeFromCSSVariables());

	const updateTheme = () => {
		setTheme(getAntdThemeFromCSSVariables());
	};
	useEffect(() => {
		updateTheme();

		// Set up a MutationObserver to watch for changes in CSS variables
		const observer = new MutationObserver(updateTheme);
		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ["style"],
		});

		// Clean up the observer when the component unmounts
		return () => observer.disconnect();
	}, []);

	return theme;
};

export const adjustColor = (hex: string, percent: number): string => {
	hex = hex.replace("#", "");

	let r = parseInt(hex.substring(0, 2), 16);
	let g = parseInt(hex.substring(2, 4), 16);
	let b = parseInt(hex.substring(4, 6), 16);

	const adjustChannel = (channel: number): number => {
		if (percent > 0) {
			return Math.max(0, Math.min(255, Math.floor(channel + (255 - channel) * (percent / 100))));
		} else {
			return Math.max(0, Math.min(255, Math.floor(channel * (1 + percent / 100))));
		}
	};

	r = adjustChannel(r);
	g = adjustChannel(g);
	b = adjustChannel(b);

	const toHex = (value: number) => value.toString(16).padStart(2, "0");
	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const getThemeNames = (): string[] => {
	return Object.keys(themeData.colors);
};

export const getThemeName = (): string => {
	return environment.REACT_APP_THEME_NAME ?? "Kural";
};

export const loadTheme = (themeName: string) => {
	const root = document.documentElement;
	const selectedTheme = themeData.colors[themeName];

	if (!selectedTheme) {
		console.error(`Theme "${themeName}" not found`);
		return;
	}

	// Load colors
	Object.entries(themeData.colors[themeName]).forEach(([key, value]: [string, string]) => {
		if (typeof value !== "boolean") root.style.setProperty(`--${key}`, value);

		if (typeof value !== "boolean" && value.startsWith("#")) {
			if (key === "primary-color" || key === "primary-color-2" || key === "secondary-color" || key === "secondary-color-2" || key === "neutral-grey") {
				const darkerShade = adjustColor(value, -20); // Darken by 20%
				const lighterShade = adjustColor(value, 10); // Lighten by 10%
				const lightShade = adjustColor(value, 25); // Lighten by 25%
				const lightestShade = adjustColor(value, 70); // Lighten by 70%

				root.style.setProperty(`--${key}-darker`, darkerShade);
				root.style.setProperty(`--${key}-light`, lightShade);
				root.style.setProperty(`--${key}-lighter`, lighterShade);
				root.style.setProperty(`--${key}-lightest`, lightestShade);

				// Generate opacity variants for each shade
				const shades = [
					{ name: "", color: value },
					{ name: "-darker", color: darkerShade },
					{ name: "-light", color: lightShade },
					{ name: "-lighter", color: lighterShade },
					{ name: "-lightest", color: lightestShade },
				];

				shades.forEach((shade) => {
					const baseColor = shade.color.slice(0, 7); // First 7 characters (#RRGGBB)
					const opacityLevels = Array.from({ length: 19 }, (_, i) => (i + 1) * 5).concat(...[3, 8]);

					opacityLevels.forEach((opacity) => {
						const hexOpacity = Math.round((opacity * 255) / 100)
							.toString(16)
							.padStart(2, "0");
						const hexWithOpacity = `${baseColor}${hexOpacity}`;
						root.style.setProperty(`--${key}${shade.name}-opacity-${opacity}`, hexWithOpacity);
					});
				});
			}

			// Generate opacity variants for non-shade colors
			if (key !== "primary-color" && key !== "secondary-color" && key !== "neutral-grey") {
				const baseColor = value.slice(0, 7); // First 7 characters (#RRGGBB)

				const opacityLevels = Array.from({ length: 19 }, (_, i) => (i + 1) * 5).concat(3, 8); // Add 8% opacity

				opacityLevels.forEach((opacity) => {
					const hexOpacity = Math.round((opacity * 255) / 100)
						.toString(16)
						.padStart(2, "0");
					const hexWithOpacity = `${baseColor}${hexOpacity}`;
					root.style.setProperty(`--${key}-opacity-${opacity}`, hexWithOpacity);
				});
			}
		}
	});

	// Load other properties
	Object.entries(themeData.other[themeName]).forEach(([key, value]: [string, string]) => {
		root.style.setProperty(`--${key}`, value);
	});

	return themeData.colors[themeName];
};
